import React, { useState, useEffect, lazy, Suspense } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import './AssessmentSubmissionsAdmin.css';

// Lazy load navbars from the correct folder relative to src/pages
const Navbar = lazy(() => import("../admin/Navbar"));
const NavbarApp = lazy(() => import("../admin/NavbarApp"));

export default function SubmissionsAdmin() {
  const location = useLocation();
  const NavbarComponent = location.pathname.includes("/app") ? NavbarApp : Navbar;

  // ── Identity (hardcoded like QNAAdminPanel) ───────────────
  // const orgCode = localStorage.getItem("orgCode");
  const orgCode = localStorage.getItem("orgCode");

  // ── State ─────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [allStudents, setAllStudents] = useState([]);
  const [courseModules, setCourseModules] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [companyName, setCompanyName] = useState('');

  // ── Fetch everything on mount ─────────────────────────────
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        // Company name
        try {
          const cRef = doc(db, `CorporateClients/${orgCode}`);
          const cSnap = await getDoc(cRef);
          if (cSnap.exists()) setCompanyName(cSnap.data().companyName || orgCode);
        } catch (_) { setCompanyName(orgCode); }

        // ── Courses + modules from programInfo ──────────────
        const progRef = collection(db, `CorporateClients/${orgCode}/programInfo`);
        const progSnap = await getDocs(progRef);
        const courseIds = [];
        progSnap.forEach(d => courseIds.push(d.id));
        courseIds.sort();

        const courseModuleList = [];
        await Promise.all(courseIds.map(async (course) => {
          const modRef = collection(db, `CorporateClients/${orgCode}/programInfo/${course}/modules`);
          const modSnap = await getDocs(modRef);
          const mods = [];
          modSnap.forEach(d => {
            const data = d.data();
            mods.push({ id: d.id, name: data.name || data.moduleName || d.id });
          });
          mods.sort((a, b) => a.name.localeCompare(b.name));
          courseModuleList.push({ course, modules: mods });
        }));
        courseModuleList.sort((a, b) => a.course.localeCompare(b.course));
        setCourseModules(courseModuleList);

        // ── All students + their submissions ─────────────────
        const stuRef = collection(db, `CorporateClients/${orgCode}/studentInfo`);
        const stuSnap = await getDocs(stuRef);
        const students = [];

        await Promise.all(stuSnap.docs.map(async (stuDoc) => {
          const data = stuDoc.data();
          const fullName = data.fullName || stuDoc.id;
          if (/excluded|lsm/i.test(fullName)) return;

          const subRef = collection(db, `CorporateClients/${orgCode}/studentInfo/${stuDoc.id}/submissions`);
          const subSnap = await getDocs(subRef);
          const submissions = {};
          subSnap.forEach(d => { submissions[d.id] = d.data(); });
          students.push({ id: stuDoc.id, fullName, submissions });
        }));

        students.sort((a, b) =>
          (a.fullName || a.id).localeCompare(b.fullName || b.id, undefined, { sensitivity: 'base' })
        );
        setAllStudents(students);
      } catch (err) {
        console.error('Error loading admin data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  // ── Derived ───────────────────────────────────────────────
  const visibleCourses = selectedCourse === 'all'
    ? courseModules
    : courseModules.filter(cm => cm.course === selectedCourse);

  // ── Render per-course table (mirrors Report.jsx assessment) ──
  const renderCourseTable = (cm) => {
    const { course, modules } = cm;
    if (modules.length === 0) return null;

    return (
      <div key={course} className="assessment-course-section">
        <div className="excel-table-wrapper">
          <table className="excel-table modules-table hierarchical">
            <thead>
              {/* Row 1: Course name spanning all module columns */}
              <tr className="course-header-row">
                <th rowSpan="2" className="sticky-col" style={{ textAlign: 'left', paddingLeft: 12 }}>
                  Student Name
                </th>
                <th colSpan={modules.length} className="course-merged-header">
                  {course}
                </th>
              </tr>

              {/* Row 2: Module names */}
              <tr className="module-header-row">
                {modules.map(mod => (
                  <th key={mod.id} className="module-merged-header">
                    {mod.name}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {allStudents.map(stu => (
                <tr key={`${course}|${stu.id}`}>
                  <td className="sticky-col student-name">{stu.fullName}</td>
                  {modules.map(mod => {
                    const key = `${course}_${mod.id}`;
                    const sub = stu.submissions[key];
                    const link = sub?.driveLink;
                    return (
                      <td key={`${stu.id}|${mod.id}`} className="submission-cell">
                        {link ? (
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sub-badge sub-badge--submitted"
                            title={`Open ${stu.fullName}'s PDF`}
                          >
                            📄 Submitted
                          </a>
                        ) : (
                          <span className="sub-badge sub-badge--missing">
                            Not Submitted
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <>
      <Suspense fallback={<div>Loading Navbar...</div>}>
        <NavbarComponent companyName={companyName} />
      </Suspense>
      <div className="container">

      {/* ── Company header (matches Report.jsx) ── */}
      <div className="company-header">
        <div className="company-name">
          <strong>Client Name : </strong>
          <span className="company-value">{companyName || orgCode}</span>
        </div>
        <div className="company-program">
          <strong>Panel : </strong>
          <span className="company-value">Submissions Admin</span>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading submissions data…</div>
      ) : (
        <>
          {/* ── Course selector (matches Report.jsx) ── */}
          <div className="tab-content">
            <div className="report-container">
              <div className="course-selector-wrapper">
                <label htmlFor="sa-course-select">Select Course:</label>
                <select
                  id="sa-course-select"
                  value={selectedCourse}
                  onChange={e => setSelectedCourse(e.target.value)}
                  className="select-dropdown"
                >
                  <option value="all">All</option>
                  {courseModules.map(cm => (
                    <option key={cm.course} value={cm.course}>{cm.course}</option>
                  ))}
                </select>
              </div>

              {allStudents.length === 0 ? (
                <div className="no-data">No student data found.</div>
              ) : visibleCourses.length === 0 ? (
                <div className="no-data">No courses found.</div>
              ) : (
                <div className="assessment-all-courses">
                  {visibleCourses.map(cm => renderCourseTable(cm))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
    </>
  );
}
